import { useEffect, Suspense, lazy } from 'react';
import { Route, Switch, Redirect, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

// Cubone FileManager
import "@cubone/react-file-manager/dist/style.css";

//React-Toastify
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import * as actions from 'store/actions';
import Links, { titles } from './utils/consts/links';
import Roles from './utils/consts/roles';
import usePrevious from './components/Hooks/usePrevious';
import PrivateRoute from 'hoc/PrivateRoute/PrivateRoute';
import cronRefreshToken from './utils/refreshToken';
import { getCookie, clearCookie, } from 'utils/cookies';
import './App.css';

//Components
const Layout = lazy(() => import('./hoc/Layout'));
const Home = lazy(() => import('./containers/Home/Home'));
const UserManagement = lazy(() => import('./containers/UserManagement/UserManagement'));
const UserRegister = lazy(() => import('./containers/UserRegister/UserRegister'));
const UserProfile = lazy(() => import('./containers/UserProfile/UserProfile'));
const Login = lazy(() => import('./containers/Login/Login'));
const Catalog = lazy(() => import('./containers/Catalog/Catalog'));
const Collections = lazy(() => import('./containers/Collections/Collections'));
const CollectionConfiguration = lazy(() => import('./containers/CollectionConfiguration/CollectionConfiguration'));
const Statistics = lazy(() => import('./containers/Statistics/features/general'));
const StatisticIngestor = lazy(() => import('./containers/Statistics/features/ingestor'));
const StatisticsStorage = lazy(() => import('./containers/StatisticsStorage'));
const StatisticsAppLayer = lazy(() => import('./containers/StatisticsAppLayer/StatisticsAppLayer'));
const ManageAccessRequests = lazy(() => import('./containers/ManageAccessRequests/ManageAccessRequests'));
const ManageChangeRequests = lazy(() => import('containers/ManageChangeRequests/ManageChangeRequests'));
const ManageSystemRequests = lazy(() => import('containers/ManageSystemRequests/ManageSystemRequests'));
const AppConfiguration = lazy(() => import('./containers/AppConfiguration/AppConfiguration'));
const LoginPassword = lazy(() => import('./containers/LoginPassword/LoginPassword'));
const IngestFile = lazy(() => import('./containers/IngestFile/IngestFile'));
const DriverRegister = lazy(() => import('./containers/DriverRegister/DriverRegister'));
const DriverConfiguration = lazy(() => import('./containers/DriverConfiguration/DriverConfiguration'));
const AssociationRegister = lazy(() => import('./containers/AssociationRegister/AssociationRegister'));
const Institutions = lazy(() => import('./containers/Institutions/Institutions'));
const InstitutionRegister = lazy(() => import('./containers/InstitutionRegister/InstitutionRegister'));
const InstitutionProfile = lazy(() => import('./containers/InstitutionProfile/InstitutionProfile'));
const UpdateExpiredPassword = lazy(() => import('./containers/UpdateExpiredPassword/UpdateExpiredPassword'));
const InternalDocumentProfile = lazy(() => import('./containers/InternalDocumentProfile/InternalDocumentProfile'));
const SystemManagement = lazy(() => import("containers/System/features/list"));
const SystemRegister = lazy(() => import("containers/System/features/create"));
const SystemProfile = lazy(() => import("containers/System/features/profile"));
const DomainManagement = lazy(() => import("containers/Domain/features/list"));
const DomainRegister = lazy(() => import("containers/Domain/features/create"));
const DomainProfile = lazy(() => import("containers/Domain/features/profile"));
const ExpressionRegister = lazy(() => import("containers/ExpressionRegister/ExpressionRegister"));
const ExpressionConfiguration = lazy(() => import("containers/ExpressionConfiguration/ExpressionConfiguration"));
const DerivativeCollections = lazy(() => import("containers/DerivativeCollections/DerivativeCollections"));
const DerivativeCollectionConfiguration = lazy(() => import("containers/DerivativeCollectionConfiguration/DerivativeCollectionConfiguration"));
const EmailConfiguration = lazy(() => import("containers/EmailConfiguration/EmailConfiguration"));
const IngestorMonitoring = lazy(() => import("containers/IngestorMonitoring/IngestorMonitoring"));
const Pipeline = lazy(() => import("containers/Pipelines/Pipelines"));
const PipelineConfiguration = lazy(() => import("containers/PipelineConfiguration/PipelineConfiguration"));
const UserPipelinesAccessRegister = lazy(() => import("containers/UserPipelinesAccessRegister/UserPipelinesAccessRegister"));
const UserPipelineAccessConfiguration = lazy(() => import("containers/UserPipelineAccessConfiguration/UserPipelineAccessConfiguration"));
const PipelineAccessRegister = lazy(() => import("containers/PipelineAccessRegister/PipelineAccessRegister"));
const PipelineAccessConfiguration = lazy(() => import("containers/PipelineAccessConfiguration/PipelineAccessConfiguration"));

function App(props) {
  const {
    verifySession,
    refreshToken,
    saveCloseTabTime,
    currentUser,
    loading,
  } = props;
  const prevLoading = usePrevious(loading);
  const shouldRedirect = getCookie('returnTo');
  //Verifica Estados de carregamento
  const isLoading = (loading || prevLoading === undefined || (currentUser && shouldRedirect));

  useEffect(() => {
    if (currentUser && shouldRedirect) {
      clearCookie('returnTo');
      window.location.href = decodeURIComponent(shouldRedirect);
    };
  }, [shouldRedirect, currentUser]);

  useEffect(() => {
    if (currentUser) {
      const handleSaveUserLastSeen = (e) => {
        e.preventDefault();
        saveCloseTabTime(currentUser.username, new Date());
      };
      window.addEventListener("beforeunload", handleSaveUserLastSeen);

      return () => {
        window.removeEventListener("beforeunload", handleSaveUserLastSeen);
      };
    }
  }, [currentUser, saveCloseTabTime]);

  useEffect(() => {
    verifySession();
  }, [verifySession]);

  useEffect(() => {
    //Torna o título da aba dinâmico para a página aberta
    const link = props.location.pathname.split("?")[0];
    const splittedDocumentTitle = document.title.split(" | ");
    document.title =
      (titles[link] ? titles[link] + " | " : "") +
      (splittedDocumentTitle[1] || splittedDocumentTitle[0]);

    //Atualiza o token de acesso automaticamente antes de 1 min do vencimento do token atual
    return cronRefreshToken(refreshToken);
  });

  const shouldComponentLoad = isLoading ? "visible" : "hidden";

  const shouldUpdatePasswordLoad =
    currentUser?.passwordValid === false ? "visible" : "hidden";

  return (
    <Suspense fallback={<div className="LoadingBackdrop"><div className="LoadingIcon" /></div>}>
      <div className="LoadingBackdrop" style={{ visibility: shouldComponentLoad }}>
        <div className="LoadingIcon" />
      </div>

      <div
        className="LoadingBackdrop"
        style={{ visibility: shouldUpdatePasswordLoad }}
      >
        <UpdateExpiredPassword open={shouldUpdatePasswordLoad === "visible"} />
        <ToastContainer />
      </div>

      <Switch>
        <Route
          exact
          path={Links.ESQUECI_SENHA}
          render={() => {
            const nextPage = props.location?.state?.from || Links.HOME;
            return !currentUser ? (
              <LoginPassword />
            ) : (
              <Redirect to={nextPage} />
            );
          }}
        />
        <Route
          exact
          path={Links.LOGIN}
          render={() => {
            if (props.location?.state?.from) {
              sessionStorage.setItem(
                "beforeLogin",
                props.location?.state?.from
              );
            }
            if (!currentUser) return <Login />;
            const nextPage =
              sessionStorage.getItem("beforeLogin") || Links.HOME;
            sessionStorage.removeItem("beforeLogin");
            return <Redirect to={nextPage} />;
          }}
        />
        <PrivateRoute
          exact
          path={Links.INGEST_FILE("/:token?")}
          component={IngestFile}
          roles={Roles.groupRegulador}
        />
        <PrivateRoute>
          <Layout>
            <Switch>
              <PrivateRoute
                exact
                path={[
                  Links.PERFIL_DOMINIO("/:domainName"),
                  Links.TOKENS_DOMINIO("/:domainName"),
                  Links.CONJUNTOS_DOMINIO("/:domainName"),
                ]}
                component={DomainProfile}
                roles={Roles.groupRegulador}
              />
              <PrivateRoute
                exact
                path={Links.CADASTRO_DOMINIO}
                component={DomainRegister}
                roles={Roles.groupRegulador}
              />
              <PrivateRoute
                exact
                path={Links.DOMINIOS()}
                component={DomainManagement}
                roles={Roles.groupRegulador}
              />
              <PrivateRoute
                exact
                path={[
                  Links.PERFIL_SISTEMA("/:systemslug"),
                  Links.TOKENS_SISTEMA("/:systemslug"),
                  Links.JDBC_SISTEMA("/:systemslug"),
                  Links.FONTE_DE_ORIGEM_SISTEMA("/:systemslug"),
                  Links.ROTA_SISTEMA("/:systemslug"),
                ]}
                component={SystemProfile}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.CADASTRO_SISTEMA}
                component={SystemRegister}
                roles={Roles.groupRegulador}
              />
              <PrivateRoute
                exact
                path={Links.SISTEMAS()}
                component={SystemManagement}
                roles={Roles.groupRegulador}
              />
              <PrivateRoute
                exact
                path={Links.APP_DOCUMENTOS_INTERNOS("/:document")}
                component={InternalDocumentProfile}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.PERFIL_INSTITUICAO("/:institutionCode")}
                component={InstitutionProfile}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.CADASTRO_INSTITUICAO}
                component={InstitutionRegister}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.INSTITUICOES()}
                component={Institutions}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.CADASTRO_ASSOCIACAO_USER_COL("/:collectionName")}
                component={AssociationRegister}
                roles={Roles.groupRegulador}
              />
              <PrivateRoute
                exact
                path={Links.APP_CONFIGURACAO_DRIVERS_EDICAO("/:driver")}
                component={DriverConfiguration}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.APP_CONFIGURACAO_EXPRESSOES_EDICAO("/:expression")}
                component={ExpressionConfiguration}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.APP_CONFIGURACAO_EMAILS_PARAMETROS("/:emailId")}
                component={EmailConfiguration}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.APP_CONFIGURACAO_DRIVERS_CADASTRO}
                component={DriverRegister}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={[
                  Links.APP_CONFIGURACAO_GERAL(),
                  Links.APP_CONFIGURACAO_DRIVERS(),
                  Links.APP_CONFIGURACAO_EXPRESSOES(),
                  Links.APP_VALIDACAO(),
                  Links.APP_DOCUMENTOS_INTERNOS(""),
                  Links.APP_CONFIGURACAO_EMAILS(""),
                  Links.APP_CONFIGURACAO_ACESSO_PIPELINES(),
                ]}
                component={AppConfiguration}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.APP_CONFIGURACAO_EXPRESSOES_CADASTRO}
                component={ExpressionRegister}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.APP_CONFIGURACAO_ACESSO_PIPELINES_CADASTRO}
                component={PipelineAccessRegister}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.APP_CONFIGURACAO_ACESSO_PIPELINES_EDICAO("/:pipelineAccessId")}
                component={PipelineAccessConfiguration}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.GERIR_PEDIDOS_DE_SISTEMA()}
                component={ManageSystemRequests}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.GERIR_PEDIDOS_DE_MUDANCA()}
                component={ManageChangeRequests}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.GERIR_PEDIDOS_DE_ACESSO()}
                component={ManageAccessRequests}
                roles={Roles.groupRegulador}
              />
              <PrivateRoute
                exact
                path={Links.MONITORAMENTO_INGESTOR}
                component={IngestorMonitoring}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.ESTATISTICA_INGESTOR()}
                component={StatisticIngestor}
                roles={Roles.groupReguladorTecnico}
              />
              <PrivateRoute
                exact
                path={Links.ESTATISTICA_ARMAZENAMENTO}
                component={StatisticsStorage}
                roles={Roles.groupReguladorTecnico}
              />
              <PrivateRoute
                exact
                path={Links.ESTATISTICA_CAMADA_SERVICO}
                component={StatisticsAppLayer}
                roles={Roles.groupReguladorTecnico}
              />
              <PrivateRoute
                exact
                path={Links.ESTATISTICAS}
                component={Statistics}
                roles={Roles.groupRegulador}
              />
              <PrivateRoute
                exact
                path={Links.UTILIZACAO_CONJUNTO_DE_DADOS()}
                component={Collections}
                roles={Roles.groupRegulador}
              />
              <PrivateRoute
                exact
                path={Links.GESTAO_DADOS()}
                component={Collections}
                roles={Roles.groupReguladorTecnico}
              />
              <PrivateRoute
                exact
                path={Links.UTILIZACAO_CONJUNTO_DE_DADOS_DERIVADOS()}
                component={DerivativeCollections}
                roles={Roles.groupRegulador}
              />
              <PrivateRoute
                exact
                path={Links.GESTAO_DADOS_DERIVADOS()}
                component={DerivativeCollections}
                roles={Roles.groupReguladorTecnico}
              />
              <PrivateRoute
                exact
                path={Links.EDIT_CONJUNTO_DADOS("/:collectionName")}
                component={CollectionConfiguration}
                roles={Roles.groupReguladorTecnico}
              />
              <PrivateRoute
                exact
                path={Links.EDIT_CONJUNTO_DADOS_DERIVADOS("/:collectionName")}
                component={DerivativeCollectionConfiguration}
                roles={Roles.groupReguladorTecnico}
              />
              <PrivateRoute
                exact
                path={Links.CATALOGO("/:collectionName?")}
                component={Catalog}
                roles={Roles.groupCommonExceptReguladorTecnico}
              />
              <PrivateRoute
                exact
                path={Links.UTILIZACAO_CONJUNTO_DE_DADOS()}
                component={Collections}
                roles={Roles.groupRegulador}
              />
              <PrivateRoute
                exact
                path={[
                  Links.PIPELINE_AGENDAMENTO(),
                  Links.GESTAO_DE_PIPELINE_INGESTAO(),
                  Links.GESTAO_DE_PIPELINE_CONSUMO(),
                  Links.PIPELINES(),
                ]}
                component={Pipeline}
                roles={Roles.groupUser}
                permission={!currentUser || currentUser?.hasPipelineAreaAccess}
              />
              <PrivateRoute
                exact
                path={Links.EDIT_PIPELINE("/:pipelineId")}
                component={PipelineConfiguration}
                roles={Roles.groupUser}
                permission={!currentUser || currentUser?.hasPipelineAreaAccess}
              />
              <PrivateRoute
                exact
                path={[
                  Links.PERFIL_USUARIO("/:username?"),
                  Links.TOKENS_USUARIO("/:username?"),
                  Links.JDBC_USUARIO("/:username?"),
                  Links.ESTATISTICA_CAMADA_DE_SERVICO_USUARIO("/:username?"),


                ]}
                component={UserProfile}
                roles={Roles.groupUser}
              />
              <PrivateRoute
                exact
                path={[
                  Links.ACESSOS_USUARIO("/:username?"),
                  Links.PEDIDOS_ACESSO_USUARIO("/:username?"),
                  Links.PEDIDOS_MUDANCA_USUARIO("/:username?"),
                  Links.ACESSO_PIPELINES_USUARIO("/:username?")
                ]}
                component={UserProfile}
                roles={Roles.groupCommonExceptReguladorTecnico}
              />
              <PrivateRoute
                exact
                path={Links.ACESSO_PIPELINES_USUARIO_CADASTRO("/:username?")}
                component={UserPipelinesAccessRegister}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={Links.ACESSO_PIPELINES_USUARIO_EDICAO("/:username?", "/:pipelineAccessId?")}
                component={UserPipelineAccessConfiguration}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute
                exact
                path={[
                  Links.USUARIOS(),
                  Links.ASSOCIACOES_USUARIOS(),
                  Links.UTILIZACAO_USUARIOS(),
                ]}
                component={UserManagement}
                roles={Roles.groupRegulador}
              />
              <PrivateRoute
                exact
                path={Links.CADASTRO_USUARIO}
                component={UserRegister}
                roles={Roles.groupAdmin}
              />
              <PrivateRoute exact path={Links.HOME} component={Home} />
              <Redirect to={Links.LOGIN} />
            </Switch>
          </Layout>
        </PrivateRoute>
      </Switch>

      <ToastContainer />
    </Suspense>
  );
}

const mapStateToProps = (state) => {
  return {
    currentUser: state.session.currentUser,
    loading: state.session.loading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    verifySession: () => dispatch(actions.verifySession()),
    refreshToken: () => dispatch(actions.refreshToken()),
    saveCloseTabTime: (username, time) =>
      dispatch(actions.saveCloseTabTime(username, time)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(App));
